import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { OPERATORS } from 'enums/operatorEnum';
import { IconButton, Layout, NumberInput, Select, Slider } from '@hub-mono/ui';
import styled from 'styled-components';

const SliderWrapper = styled(Layout)`
    max-width: 670px;
    width: 100%;
`;

function transformScaledValueToValue(scaledValue, marks) {
    for (let i = 0; i < marks.length - 1; i++) {
        const currentMark = marks[i];
        const nextMark = marks[i + 1];

        if (scaledValue >= currentMark.scaledValue && scaledValue <= nextMark.scaledValue) {
            const proportion =
                (scaledValue - currentMark.scaledValue) / (nextMark.scaledValue - currentMark.scaledValue);
            return currentMark.value + proportion * (nextMark.value - currentMark.value);
        }
    }

    if (scaledValue < marks[0].scaledValue) {
        return marks[0].value;
    }
    if (scaledValue > marks[marks.length - 1].scaledValue) {
        return marks[marks.length - 1].value;
    }
}

function transformValueToScaledValue(value, marks) {
    for (let i = 0; i < marks.length - 1; i++) {
        const currentMark = marks[i];
        const nextMark = marks[i + 1];

        if (value >= currentMark.value && value <= nextMark.value) {
            const proportion = (value - currentMark.value) / (nextMark.value - currentMark.value);
            return Math.floor(currentMark.scaledValue + proportion * (nextMark.scaledValue - currentMark.scaledValue));
        }
    }

    if (value < marks[0].value) {
        return marks[0].scaledValue;
    }
    if (value > marks[marks.length - 1].value) {
        return marks[marks.length - 1].scaledValue;
    }
}

const SliderFilter = props => {
    const { defaultValues, onChange, marks, title, isAvailabilityCase, disabled, min, max } = props;

    const [availabilityValue, setAvailabilityValue] = useState(
        defaultValues.value !== '' || defaultValues.value === 0 ? defaultValues.value : 100
    );

    const [sliderValue, setSliderValue] = useState(
        defaultValues.value !== '' || defaultValues.value === 0
            ? transformScaledValueToValue(defaultValues.value, marks)
            : 100
    );

    const numFormatter = num => {
        if (num > 999) {
            return (num / 1000).toFixed(0) + 'K';
        } else {
            return num; // if value < 1000, nothing to do
        }
    };

    const handleOperatorSelect = useCallback(
        (event, value) => {
            onChange && onChange({ operator: value.value });
        },
        [onChange]
    );

    const setValue = useCallback(
        value => {
            if (onChange) {
                // sending scaled
                onChange({ value });
            }
        },
        [onChange]
    );

    const handleSliderChange = useCallback(
        (event, newValue) => {
            const newAvailabilityValue = transformValueToScaledValue(newValue, marks);

            setValue(newAvailabilityValue);
            setSliderValue(newValue);
            setAvailabilityValue(newAvailabilityValue);
        },
        [setValue, marks]
    );

    const handleInputChange = useCallback(
        event => {
            const sliderValue = transformScaledValueToValue(
                '' === event.target.value ? 0 : Number(event.target.value),
                marks
            );
            const newValue = '' === event.target.value ? '' : Number(event.target.value);

            setValue('' === event.target.value ? '' : Number(event.target.value));
            setSliderValue(sliderValue);
            setAvailabilityValue(newValue);
        },
        [setValue, marks]
    );

    const valuetext = useCallback(value => {
        return `${value}%`;
    }, []);

    const selectedOperator = useMemo(() => {
        return OPERATORS.find(option => option.value === defaultValues.operator) || OPERATORS[0];
    }, [defaultValues.operator]);

    useEffect(() => {
        const { value, operator } = defaultValues || {};

        if (value !== undefined && typeof value === 'number' && operator) {
            setSliderValue(transformScaledValueToValue(value, marks));
            setAvailabilityValue(value);
        } else {
            setSliderValue(0);
            setAvailabilityValue(value);
        }
    }, [defaultValues, marks]);

    const sliderVariant = useMemo(() => {
        if (isAvailabilityCase) {
            return 'green';
        }

        if (availabilityValue > 100) {
            return 'red';
        }

        if (availabilityValue < 50) {
            return 'blue';
        }

        return 'green';
    }, [isAvailabilityCase, availabilityValue]);

    return (
        <Layout stack hAlign="center">
            <SliderWrapper gap={24} hAlign="center">
                <Select
                    disabled={disabled}
                    dataCy="operator-dropdown-capacity-filter"
                    value={selectedOperator}
                    onChange={handleOperatorSelect}
                    options={OPERATORS}
                    label={title}
                    optionLabel="name"
                    optionValue="value"
                    disableClearable
                />
                <IconButton variant="outlined" disabled={disabled} icon={selectedOperator.icon} />
                <NumberInput
                    onKeyPress={e => {
                        if (e.key === '-' || e.key === 'e') {
                            e.preventDefault();
                        }
                    }}
                    name={`${title}-input`}
                    dataCy={`${title}-input`}
                    min={0}
                    placeholder="100"
                    value={availabilityValue}
                    label={isAvailabilityCase ? 'Hours' : 'Percentage'}
                    onChange={handleInputChange}
                    disabled={disabled}
                    endAdornment={!isAvailabilityCase ? '%' : undefined}
                />
            </SliderWrapper>
            <SliderWrapper stack stretch pt={24}>
                <Slider
                    variant={sliderVariant}
                    value={sliderValue}
                    getAriaValueText={valuetext}
                    step={1}
                    valueLabelDisplay="on"
                    marks={marks}
                    isAvailabilityCase={isAvailabilityCase}
                    onChange={handleSliderChange}
                    disabled={disabled}
                    min={min}
                    max={max}
                    scale={() => availabilityValue || 0}
                    valueLabelFormat={numFormatter}
                />
            </SliderWrapper>
        </Layout>
    );
};

SliderFilter.propTypes = {
    defaultValues: PropTypes.shape({
        operator: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    onChange: PropTypes.func,
    clearBtn: PropTypes.bool,
    title: PropTypes.string,
    marks: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
};

SliderFilter.defaultProps = {
    onChange: null,
    clearBtn: false,
    defaultValues: {
        operator: '',
        value: '',
    },
    disabled: false,
    min: 0,
    max: 100,
};

const memoized = React.memo(SliderFilter);

export { memoized as SliderFilterV2 };
